import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, ImagePlus, Trash, Sparkles, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GENERATE_AD_URL, GENERATE_AUTH_TOKEN } from '../config';
import LoadingAnimation from '../components/LoadingAnimation';

export const AddFirstProduct = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productName, setProductName] = useState('');
  const [productImageUrl, setProductImageUrl] = useState<string | null>(null);
  const [productImageFile, setProductImageFile] = useState<File | null>(null);
  const [generating, setGenerating] = useState(false);
  const [completeProgressNow, setCompleteProgressNow] = useState(false);
  const [showInstructionsPopup, setShowInstructionsPopup] = useState(false);
  const [userRubro, setUserRubro] = useState<string>('Moda e Indumentaria');
  const willShowResultsRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch user's rubro on mount
  useEffect(() => {
    const fetchRubro = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();

        if (!(profile as any)?.id_company) return;

        const { data: company } = await supabase
          .from('company_profiles')
          .select('rubro')
          .eq('id_company', (profile as any).id_company)
          .single();

        if (company?.rubro) {
          setUserRubro(company.rubro as string);
        }
      } catch (err) {
        // Keep default rubro on error
      }
    };
    fetchRubro();
  }, []);

  // Determine which images and instructions to show based on rubro
  const isFashion = userRubro === 'Moda e Indumentaria';
  const isHomeDecor = userRubro === 'Hogar y Decoración';
  
  const getExampleImages = () => {
    if (isFashion) {
      return {
        img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso.jpeg',
        img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso_siendo_usado.png',
        alt1: 'Ejemplo de foto de producto',
        alt2: 'Ejemplo de foto de producto siendo utilizado'
      };
    } else if (isHomeDecor) {
      return {
        img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon.png',
        img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon_casa.png',
        alt1: 'Ejemplo de foto de producto',
        alt2: 'Ejemplo de foto de producto en contexto'
      };
    } else {
      return {
        img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella.png',
        img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella_uso.png',
        alt1: 'Ejemplo de foto de producto',
        alt2: 'Ejemplo de foto de producto en contexto'
      };
    }
  };
  
  const exampleImages = getExampleImages();

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('El archivo es demasiado grande. El tamaño máximo es 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);

      // Store file for later upload and create preview URL
      setProductImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setProductImageUrl(previewUrl);

    } catch (err) {
      console.error('Error processing image:', err);
      setError(err instanceof Error ? err.message : 'Error al procesar la imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setProductImageUrl(null);
    setProductImageFile(null);
  };

  const handleGenerateImage = async () => {
    if (!productName.trim() || !productImageFile) return;

    try {
      setLoading(true);
      setGenerating(true);
      setCompleteProgressNow(false);
      setError(null);

      // 1) Get user and company
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No se encontró la empresa del usuario');
      const idCompany = (profile as any).id_company as string;

      // 2) Create product
      const { data: producto, error: productoError } = await supabase
        .from('productos')
        .insert({ id_company: idCompany, nombre: productName.trim(), tiene_variantes: false })
        .select('id')
        .single();
      if (productoError) throw productoError;

      const productId = String(producto.id);

      // 3) Create variant for that product
      const { data: variant, error: variantError } = await supabase
        .from('variantes')
        .insert({ id_company: idCompany, nombre: productName.trim(), activo: true, product_id: productId })
        .select('id')
        .single();
      if (variantError) throw variantError;

      const variantId = variant.id as string;

      // 4) Upload image to productos bucket
      const timestamp = Date.now();
      const originalName = productImageFile.name;
      const fileBaseName = `${timestamp}_${originalName}`;

      // Upload full image
      const fullPath = `${idCompany}/${variantId}/full/${fileBaseName}`;
      const { error: fullErr } = await supabase.storage.from('productos').upload(fullPath, productImageFile);
      if (fullErr) throw fullErr;

      // Create optimized thumbnail
      try {
        const img = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get canvas context');

        const blob = await new Promise<Blob>((resolve, reject) => {
          img.onload = () => {
            const target = 300;
            let { width, height } = img;
            if (width > height) {
              if (width > target) {
                height = Math.round(height * (target / width));
                width = target;
              }
            } else if (height > target) {
              width = Math.round(width * (target / height));
              height = target;
            }
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Blob failed')), 'image/jpeg', 0.8);
          };
          img.onerror = () => reject(new Error('Image load error'));
          img.src = URL.createObjectURL(productImageFile);
        });

        const optPath = `${idCompany}/${variantId}/opt/${fileBaseName}`;
        await supabase.storage.from('productos').upload(optPath, blob);
      } catch (optErr) {
        console.warn('Error creating optimized image, continuing', optErr);
      }

      // 5) Insert image record in DB
      const { error: imageError } = await supabase
        .from('productos_imagenes')
        .insert({ id_company: idCompany, variante_id: variantId, url: fileBaseName, principal: true, orden: 1 });
      if (imageError) throw imageError;

      // 6) Notify categorization webhook (fire and forget)
      try {
        await fetch('https://ai-agency-dt7d.onrender.com/webhook/categorizar_producto', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
          body: JSON.stringify({ user_id: user.id, product_id: productId })
        });
      } catch {}

      // 7) Generate image with automatic mode and model
      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: [variantId],
        mode: 'single',
        modo: 'rapido',
        estilo: 'automatico',
        tipo: 'imagen',
        cantidad_imagenes: 2,
        Format: '4:5',
        composicion: 'modelo',
        modo_pro: false
      };

      console.log('[AddFirstProduct] handleGenerateImage payload', requestData);
      const response = await fetch(GENERATE_AD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        console.error('[AddFirstProduct] handleGenerateImage not ok');
        throw new Error('Error al generar imágenes');
      }

      let data: any = null;
      try { data = await response.json(); } catch (e) { console.error('[AddFirstProduct] json parse error', e); }
      console.log('[AddFirstProduct] handleGenerateImage response', data);

      if (data?.response === 'success' && Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        willShowResultsRef.current = true;
        const imageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult = (data.id_company as string) || idCompany;
        setCompleteProgressNow(true);
        
        // Navigate to generated images page with lastPayload for "Generar más"
        navigate('/redes-sociales/imagenes-generadas', {
          state: { imageIds, companyId: companyIdResult, lastPayload: requestData }
        });
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }

    } catch (err) {
      console.error('Error generating image:', err);
      setError(err instanceof Error ? err.message : 'Error al generar la imagen');
      setGenerating(false);
      setCompleteProgressNow(false);
    } finally {
      setLoading(false);
    }
  };

  const canGenerate = productName.trim() !== '' && productImageFile !== null;

  return (
    <>
      {/* Loading Animation - 75 seconds (1 min 15 seg) */}
      {generating && (
        <LoadingAnimation
          message="Generando tu primera imagen..."
          subMessage="Esto puede tomar hasta un minuto"
          durationMs={75000}
          completeNow={completeProgressNow}
          onCompleted={() => {
            if (!willShowResultsRef.current) {
              setGenerating(false);
            }
          }}
        />
      )}

      <div className="min-h-screen bg-white md:bg-[#EFF0F5] flex items-center justify-center p-4">
        <div className="w-full max-w-md md:max-w-2xl">
          <div className="bg-white rounded-none md:rounded-xl shadow-none md:shadow-lg p-8">
            <h2 className="text-3xl md:text-2xl font-semibold text-gray-900 mb-2">
              Agregá tu primer producto
            </h2>
            <p className="text-gray-500 mb-8">
              Subí una foto de tu producto y generá tu primera imagen con IA
            </p>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            <div className="space-y-6">
              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del producto
                </label>
                <input
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Ej: Remera básica negra"
                  className="w-full h-12 px-5 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              {/* Product Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Foto del producto
                </label>
                {productImageUrl ? (
                  <div className="relative w-40 h-40 rounded-lg overflow-hidden border border-gray-200">
                    <img 
                      src={productImageUrl} 
                      alt="Product" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
                    >
                      <Trash className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="file"
                      id="product-image-upload"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                    <button
                      type="button"
                      onClick={() => setShowInstructionsPopup(true)}
                      className={`flex items-center md:justify-center gap-2 h-12 px-6 border border-gray-300 rounded-full cursor-pointer hover:bg-gray-50 transition-colors w-full md:w-auto ${
                        uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <ImagePlus className="w-5 h-5" />
                          Subir foto
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Action Button */}
              <div className="pt-4 flex md:justify-end">
                <button
                  type="button"
                  onClick={handleGenerateImage}
                  disabled={!canGenerate || loading}
                  className="w-full md:w-auto h-12 px-6 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generar imagen
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions Popup */}
      {showInstructionsPopup && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-lg p-6 relative">
            <button
              type="button"
              onClick={() => setShowInstructionsPopup(false)}
              className="absolute top-3 right-3 p-2 text-gray-500 hover:text-gray-700 rounded-md focus:outline-none"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Cómo subir una buena foto
            </h3>
            
            {/* Example images */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 min-w-0 flex flex-col items-center gap-0">
                <div className="w-full aspect-square rounded-lg border border-gray-200 bg-white overflow-hidden relative">
                  <img
                    src={exampleImages.img1}
                    alt={exampleImages.alt1}
                    loading="lazy"
                    className="w-full h-full object-contain"
                  />
                  <span aria-label="Correcta" className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white shadow-sm">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
              <div className="flex-1 min-w-0 flex flex-col items-center gap-0">
                <div className="w-full aspect-square rounded-lg border border-gray-200 bg-white overflow-hidden relative">
                  <img
                    src={exampleImages.img2}
                    alt={exampleImages.alt2}
                    loading="lazy"
                    className="w-full h-full object-contain"
                  />
                  <span aria-label="Correcta" className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white shadow-sm">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </div>
            
            {/* Instructions */}
            <ul className="text-sm text-gray-600 list-disc pl-4 space-y-2 mb-6">
              <li>Subí una foto en donde se vea claramente el producto que querés vender.</li>
              {isFashion && <li>Fotos del producto siendo utilizado (sin mostrar el rostro) funcionan mejor.</li>}
            </ul>
            
            <button
              type="button"
              onClick={() => {
                setShowInstructionsPopup(false);
                fileInputRef.current?.click();
              }}
              className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-sm font-medium shadow-sm transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AddFirstProduct;
